// ============================================================================
// KANBAN SHARED TYPES & CONSTANTS
// Shared between WorkItemsKanbanBoard and TimeAllocationView
// Extracted: February 2026
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

/** Workflow state definition for Kanban columns */
export interface WorkflowState {
  key: string;
  label: string;
  color: string;
  description?: string;
  isSystem?: boolean;
  userId?: number; // For user columns in time allocation view
  resourceType?: 'user' | 'team';
  resourceId?: number | string;
  teamId?: string;
  leaderUserId?: number | null;
  memberUserIds?: number[];
}

/** User type for assignment columns */
export interface AssignedUser {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  isInactive?: boolean;
}

/** Leave data for time allocation view - shows approved leave as unavailable time */
export interface UserLeave {
  id: number;
  user_id: number;
  employee_name: string;
  leave_type_name: string;
  leave_type_color?: string;
  start_date: string;  // ISO date string
  end_date: string;    // ISO date string
  is_half_day: boolean;
  half_day_period?: 'morning' | 'afternoon';
  total_days: number;
  status: 'approved';  // We only fetch approved leave
}

/** Zoom levels for Kanban board display density */
export type ZoomLevel = 'compact' | 'normal' | 'expanded' | 'mobile';

// ============================================================================
// ZOOM SETTINGS
// ============================================================================

export const ZOOM_SETTINGS = {
  compact: {
    columnWidth: 'w-56',
    cardPadding: 'p-2',
    columnPadding: 'p-1.5',
    headerPadding: 'p-2',
    fontSize: 'text-xs',
    headerText: 'text-sm',
    badgeSize: 'text-xs',
    showDescription: false,
    showTags: false,
    showProgress: false,
    label: 'Compact'
  },
  normal: {
    columnWidth: 'w-72',
    cardPadding: 'p-3',
    columnPadding: 'p-2',
    headerPadding: 'p-3',
    fontSize: 'text-sm',
    headerText: 'text-base',
    badgeSize: 'text-xs',
    showDescription: true,
    showTags: true,
    showProgress: true,
    label: 'Normal'
  },
  expanded: {
    columnWidth: 'w-80',
    cardPadding: 'p-4',
    columnPadding: 'p-3',
    headerPadding: 'p-4',
    fontSize: 'text-sm',
    headerText: 'text-lg',
    badgeSize: 'text-sm',
    showDescription: true,
    showTags: true,
    showProgress: true,
    label: 'Expanded'
  },
  mobile: {
    columnWidth: 'w-72',
    cardPadding: 'p-3',
    columnPadding: 'p-2',
    headerPadding: 'p-2',
    fontSize: 'text-sm',
    headerText: 'text-sm',
    badgeSize: 'text-xs',
    showDescription: true,
    showTags: false,
    showProgress: true,
    label: 'Mobile'
  }
} as const;

// ============================================================================
// AVATAR HELPERS (consistent with WorkItemsDataGrid)
// ============================================================================

export const AVATAR_COLORS_KB = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500',
  'bg-orange-500', 'bg-pink-500', 'bg-teal-500'
];

export function getKBAvatarColor(email?: string, firstName?: string): string {
  const code = ((email?.charCodeAt(0) || 0) + (firstName?.charCodeAt(0) || 0)) % AVATAR_COLORS_KB.length;
  return AVATAR_COLORS_KB[code];
}

export function getKBInitials(firstName?: string, lastName?: string, email?: string): string {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.substring(0, 2).toUpperCase();
  if (email) return email.substring(0, 2).toUpperCase();
  return '??';
}

export function getKBInitialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].substring(0, 2).toUpperCase();
  return '??';
}

// ============================================================================
// COLOR CLASSES - Color mapping for workflow state columns (dark mode support)
// ============================================================================

export const COLOR_CLASSES: Record<string, {
  gradient: string;
  bg: string;
  header: string;
  border: string;
  text: string;
  icon: string;
  iconBg: string;
  badge: string;
  headingText: string;
  subtitleText: string;
}> = {
  gray: {
    gradient: 'from-gray-400 to-slate-500',
    bg: 'bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800',
    header: 'bg-gradient-to-br from-gray-50 to-slate-100 dark:from-gray-800 dark:to-slate-900',
    border: 'border-gray-300 dark:border-gray-600',
    text: 'text-gray-700 dark:text-gray-300',
    icon: 'text-gray-600 dark:text-gray-400',
    iconBg: 'bg-gradient-to-r from-gray-400 to-slate-500',
    badge: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    headingText: 'text-gray-900 dark:text-gray-100',
    subtitleText: 'text-gray-600 dark:text-gray-400'
  },
  blue: {
    gradient: 'from-blue-400 to-indigo-500',
    bg: 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/40 dark:to-indigo-900/40',
    header: 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/50 dark:to-indigo-900/50',
    border: 'border-blue-300 dark:border-blue-700',
    text: 'text-blue-700 dark:text-blue-300',
    icon: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-gradient-to-r from-blue-400 to-indigo-500',
    badge: 'bg-blue-200 text-blue-700 dark:bg-blue-800 dark:text-blue-200',
    headingText: 'text-blue-900 dark:text-blue-100',
    subtitleText: 'text-blue-700 dark:text-blue-300'
  },
  yellow: {
    gradient: 'from-yellow-400 to-orange-500',
    bg: 'bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/40 dark:to-orange-900/40',
    header: 'bg-gradient-to-br from-yellow-50 to-orange-100 dark:from-yellow-900/50 dark:to-orange-900/50',
    border: 'border-yellow-300 dark:border-yellow-700',
    text: 'text-yellow-700 dark:text-yellow-300',
    icon: 'text-yellow-600 dark:text-yellow-400',
    iconBg: 'bg-gradient-to-r from-yellow-400 to-orange-500',
    badge: 'bg-yellow-200 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200',
    headingText: 'text-yellow-900 dark:text-yellow-100',
    subtitleText: 'text-yellow-700 dark:text-yellow-300'
  },
  orange: {
    gradient: 'from-orange-400 to-red-500',
    bg: 'bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/40 dark:to-red-900/40',
    header: 'bg-gradient-to-br from-orange-50 to-red-100 dark:from-orange-900/50 dark:to-red-900/50',
    border: 'border-orange-300 dark:border-orange-700',
    text: 'text-orange-700 dark:text-orange-300',
    icon: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-gradient-to-r from-orange-400 to-red-500',
    badge: 'bg-orange-200 text-orange-700 dark:bg-orange-800 dark:text-orange-200',
    headingText: 'text-orange-900 dark:text-orange-100',
    subtitleText: 'text-orange-700 dark:text-orange-300'
  },
  purple: {
    gradient: 'from-purple-400 to-pink-500',
    bg: 'bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/40 dark:to-pink-900/40',
    header: 'bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/50 dark:to-pink-900/50',
    border: 'border-purple-300 dark:border-purple-700',
    text: 'text-purple-700 dark:text-purple-300',
    icon: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-gradient-to-r from-purple-400 to-pink-500',
    badge: 'bg-purple-200 text-purple-700 dark:bg-purple-800 dark:text-purple-200',
    headingText: 'text-purple-900 dark:text-purple-100',
    subtitleText: 'text-purple-700 dark:text-purple-300'
  },
  green: {
    gradient: 'from-green-400 to-emerald-500',
    bg: 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/40 dark:to-emerald-900/40',
    header: 'bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/50 dark:to-emerald-900/50',
    border: 'border-green-300 dark:border-green-700',
    text: 'text-green-700 dark:text-green-300',
    icon: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-gradient-to-r from-green-400 to-emerald-500',
    badge: 'bg-green-200 text-green-700 dark:bg-green-800 dark:text-green-200',
    headingText: 'text-green-900 dark:text-green-100',
    subtitleText: 'text-green-700 dark:text-green-300'
  },
  red: {
    gradient: 'from-red-400 to-rose-500',
    bg: 'bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/40 dark:to-rose-900/40',
    header: 'bg-gradient-to-br from-red-50 to-rose-100 dark:from-red-900/50 dark:to-rose-900/50',
    border: 'border-red-300 dark:border-red-700',
    text: 'text-red-700 dark:text-red-300',
    icon: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-gradient-to-r from-red-400 to-rose-500',
    badge: 'bg-red-200 text-red-700 dark:bg-red-800 dark:text-red-200',
    headingText: 'text-red-900 dark:text-red-100',
    subtitleText: 'text-red-700 dark:text-red-300'
  },
  teal: {
    gradient: 'from-teal-400 to-cyan-500',
    bg: 'bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/40 dark:to-cyan-900/40',
    header: 'bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-teal-900/50 dark:to-cyan-900/50',
    border: 'border-teal-300 dark:border-teal-700',
    text: 'text-teal-700 dark:text-teal-300',
    icon: 'text-teal-600 dark:text-teal-400',
    iconBg: 'bg-gradient-to-r from-teal-400 to-cyan-500',
    badge: 'bg-teal-200 text-teal-700 dark:bg-teal-800 dark:text-teal-200',
    headingText: 'text-teal-900 dark:text-teal-100',
    subtitleText: 'text-teal-700 dark:text-teal-300'
  },
  indigo: {
    gradient: 'from-indigo-400 to-blue-500',
    bg: 'bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/40 dark:to-blue-900/40',
    header: 'bg-gradient-to-br from-indigo-50 to-blue-100 dark:from-indigo-900/50 dark:to-blue-900/50',
    border: 'border-indigo-300 dark:border-indigo-700',
    text: 'text-indigo-700 dark:text-indigo-300',
    icon: 'text-indigo-600 dark:text-indigo-400',
    iconBg: 'bg-gradient-to-r from-indigo-400 to-blue-500',
    badge: 'bg-indigo-200 text-indigo-700 dark:bg-indigo-800 dark:text-indigo-200',
    headingText: 'text-indigo-900 dark:text-indigo-100',
    subtitleText: 'text-indigo-700 dark:text-indigo-300'
  },
  cyan: {
    gradient: 'from-cyan-400 to-teal-500',
    bg: 'bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/40 dark:to-teal-900/40',
    header: 'bg-gradient-to-br from-cyan-50 to-teal-100 dark:from-cyan-900/50 dark:to-teal-900/50',
    border: 'border-cyan-300 dark:border-cyan-700',
    text: 'text-cyan-700 dark:text-cyan-300',
    icon: 'text-cyan-600 dark:text-cyan-400',
    iconBg: 'bg-gradient-to-r from-cyan-400 to-teal-500',
    badge: 'bg-cyan-200 text-cyan-700 dark:bg-cyan-800 dark:text-cyan-200',
    headingText: 'text-cyan-900 dark:text-cyan-100',
    subtitleText: 'text-cyan-700 dark:text-cyan-300'
  },
  emerald: {
    gradient: 'from-emerald-400 to-green-500',
    bg: 'bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/40 dark:to-green-900/40',
    header: 'bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/50 dark:to-green-900/50',
    border: 'border-emerald-300 dark:border-emerald-700',
    text: 'text-emerald-700 dark:text-emerald-300',
    icon: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-gradient-to-r from-emerald-400 to-green-500',
    badge: 'bg-emerald-200 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200',
    headingText: 'text-emerald-900 dark:text-emerald-100',
    subtitleText: 'text-emerald-700 dark:text-emerald-300'
  },
  violet: {
    gradient: 'from-violet-400 to-purple-500',
    bg: 'bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/40 dark:to-purple-900/40',
    header: 'bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/50 dark:to-purple-900/50',
    border: 'border-violet-300 dark:border-violet-700',
    text: 'text-violet-700 dark:text-violet-300',
    icon: 'text-violet-600 dark:text-violet-400',
    iconBg: 'bg-gradient-to-r from-violet-400 to-purple-500',
    badge: 'bg-violet-200 text-violet-700 dark:bg-violet-800 dark:text-violet-200',
    headingText: 'text-violet-900 dark:text-violet-100',
    subtitleText: 'text-violet-700 dark:text-violet-300'
  },
  amber: {
    gradient: 'from-amber-400 to-yellow-500',
    bg: 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/40 dark:to-yellow-900/40',
    header: 'bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/50 dark:to-yellow-900/50',
    border: 'border-amber-300 dark:border-amber-700',
    text: 'text-amber-700 dark:text-amber-300',
    icon: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-gradient-to-r from-amber-400 to-yellow-500',
    badge: 'bg-amber-200 text-amber-700 dark:bg-amber-800 dark:text-amber-200',
    headingText: 'text-amber-900 dark:text-amber-100',
    subtitleText: 'text-amber-700 dark:text-amber-300'
  },
  slate: {
    gradient: 'from-slate-400 to-gray-500',
    bg: 'bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/40 dark:to-gray-900/40',
    header: 'bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/50 dark:to-gray-900/50',
    border: 'border-slate-300 dark:border-slate-700',
    text: 'text-slate-700 dark:text-slate-300',
    icon: 'text-slate-600 dark:text-slate-400',
    iconBg: 'bg-gradient-to-r from-slate-400 to-gray-500',
    badge: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    headingText: 'text-slate-900 dark:text-slate-100',
    subtitleText: 'text-slate-700 dark:text-slate-300'
  }
};

// ============================================================================
// PRIORITY COLORS FOR TIME ALLOCATION VIEW
// ============================================================================

export const TIME_ALLOC_PRIORITY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  critical: { bg: 'bg-red-100 dark:bg-red-900/30', border: 'border-red-400', text: 'text-red-800 dark:text-red-200' },
  urgent: { bg: 'bg-orange-100 dark:bg-orange-900/30', border: 'border-orange-400', text: 'text-orange-800 dark:text-orange-200' },
  high: { bg: 'bg-amber-100 dark:bg-amber-900/30', border: 'border-amber-400', text: 'text-amber-800 dark:text-amber-200' },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', border: 'border-yellow-400', text: 'text-yellow-800 dark:text-yellow-200' },
  low: { bg: 'bg-green-100 dark:bg-green-900/30', border: 'border-green-400', text: 'text-green-800 dark:text-green-200' },
};

// ============================================================================
// WORK ITEMS VIEW TOOLBAR CHROME (List / Board / Schedule)
// Shared visual language: WorkItemsKanbanBoard, ResourceScheduleTimeline, and
// aligned with WorkItemsDataGrid surface header density — February 2026
// ============================================================================

/** Card shell used above Kanban columns (standalone board toolbar) */
export const WORK_ITEMS_VIEW_TOOLBAR_CARD_CLASS =
  'mb-2 sm:mb-4 rounded-xl border border-gray-200 bg-white p-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-3';

/**
 * Same card treatment when the toolbar sits above sidebar + timeline (embedded).
 * Keeps spacing below the bar without double margins from the parent flex.
 */
export const WORK_ITEMS_VIEW_TOOLBAR_CARD_EMBEDDED_CLASS =
  'mx-0 mb-2 shrink-0 rounded-xl border border-gray-200 bg-white p-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:mb-3 sm:p-3';

export const WORK_ITEMS_VIEW_TOOLBAR_INNER_ROW_CLASS = 'flex flex-wrap items-center gap-2';

/** Primary view label pill — "• JOBS — STATUS BOARD" */
export const WORK_ITEMS_VIEW_TITLE_PILL_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-gray-700 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-200';

/** Zoom, date steppers, grouped icon controls */
export const WORK_ITEMS_VIEW_TOOLBAR_GROUP_CLASS =
  'flex flex-wrap items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50/80 px-2 py-1 dark:border-gray-600 dark:bg-gray-700/50';

export const WORK_ITEMS_VIEW_TOOLBAR_ICON_BUTTON_CLASS =
  'rounded-lg p-1.5 transition-colors touch-manipulation';

export const WORK_ITEMS_VIEW_BOARD_PREFS_BUTTON_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50/90 px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-200 dark:hover:bg-gray-700';

/** List / grid “surface” strip — pairs with rounded grid container (WorkItemsDataGrid) */
export const WORK_ITEMS_VIEW_SURFACE_HEADER_CLASS =
  'flex items-center justify-between gap-3 rounded-t-lg border-b border-gray-200/90 bg-gradient-to-r from-gray-50/95 via-white to-gray-50/80 px-3 py-2.5 sm:px-4 dark:border-gray-700 dark:from-gray-900/50 dark:via-gray-900/30 dark:to-gray-900/50';
